interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
  source: string;
}

export const CACHE_TTL = {
  weather: 15 * 60 * 1000,      // 15 minutes
  location: 60 * 60 * 1000,     // 1 hour
  gps: 30 * 60 * 1000,          // 30 minutes
};

export class StorageHelper {
  private static isLocalStorageAvailable(): boolean {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  static set<T>(key: string, data: T, ttl: number, source: string = 'unknown'): void {
    if (!this.isLocalStorageAvailable()) {
      console.warn('localStorage is not available');
      return;
    }

    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttl,
      source,
    };

    try {
      localStorage.setItem(key, JSON.stringify(entry));
    } catch (e) {
      console.error('Failed to save to localStorage:', e);
      // Storage quota exceeded - clear old entries
      this.clearExpired();
    }
  }

  static get<T>(key: string): T | null {
    if (!this.isLocalStorageAvailable()) {
      return null;
    }

    try {
      const item = localStorage.getItem(key);
      if (!item) return null;

      const entry: CacheEntry<T> = JSON.parse(item);

      // Check if expired
      if (Date.now() > entry.expiresAt) {
        localStorage.removeItem(key);
        return null;
      }

      return entry.data;
    } catch (e) {
      console.error('Failed to read from localStorage:', e);
      return null;
    }
  }

  static getCacheInfo<T>(key: string): { data: T; age: number; source: string } | null {
    if (!this.isLocalStorageAvailable()) {
      return null;
    }

    try {
      const item = localStorage.getItem(key);
      if (!item) return null;

      const entry: CacheEntry<T> = JSON.parse(item);

      // Check if expired
      if (Date.now() > entry.expiresAt) {
        localStorage.removeItem(key);
        return null;
      }

      return {
        data: entry.data,
        age: Date.now() - entry.timestamp,
        source: entry.source,
      };
    } catch (e) {
      console.error('Failed to read cache info:', e);
      return null;
    }
  }

  static remove(key: string): void {
    if (!this.isLocalStorageAvailable()) {
      return;
    }
    localStorage.removeItem(key);
  }

  static clearExpired(): void {
    if (!this.isLocalStorageAvailable()) {
      return;
    }

    const keys = Object.keys(localStorage);
    const now = Date.now();

    keys.forEach(key => {
      try {
        const item = localStorage.getItem(key);
        if (!item) return;

        const entry: CacheEntry<any> = JSON.parse(item);
        if (now > entry.expiresAt) {
          localStorage.removeItem(key);
        }
      } catch (e) {
        // Invalid entry, remove it
        localStorage.removeItem(key);
      }
    });
  }

  static clear(): void {
    if (!this.isLocalStorageAvailable()) {
      return;
    }
    localStorage.clear();
  }

  static getSize(): number {
    if (!this.isLocalStorageAvailable()) {
      return 0;
    }

    let size = 0;
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        size += localStorage[key].length + key.length;
      }
    }
    return size;
  }
}
